import { CSSProperties } from "react";
import BlurredPlaceholder from "@/app/(components)/generics/BlurredPlaceholder/BlurredPlaceholder";
import { useT } from "@/app/translations/useT";
import {
  ProspectCriteriaField,
  ProspectSearchCriteria,
} from "@/app/types/prospect/ProspectSearchCriteria";
import { UStyles } from "@/app/utils/UStyles";
import { categoriesLayoutClass, ProspectStrategyGroup } from "./ProspectStrategy.logic";
import styles from "./ProspectStrategy.module.css";
import ProspectStrategyValues from "./ProspectStrategyValues";

type Props = {
  group: ProspectStrategyGroup;
  lang: string;
  criteria?: ProspectSearchCriteria | null;
  isPlaceholder?: boolean;
  onSaveField?: (field: ProspectCriteriaField, items: string[]) => void;
};

export default function ProspectStrategyGroupPanel(props: Props) {
  const T = useT();
  const { group, lang, criteria, isPlaceholder = false, onSaveField } = props;
  const showCategoryHeader = group.categories.length > 1;

  // Read-only real data: if the whole group is empty, hide it (title included)
  // so no orphan header is left. The teaser keeps showing its placeholders.
  if (!onSaveField && !isPlaceholder) {
    const groupHasValues = group.categories.some(
      (category) => (criteria?.[category.field]?.length ?? 0) > 0,
    );
    if (!groupHasValues) {
      return null;
    }
  }

  return (
    <div className={styles.group}>
      <div
        className={UStyles.classNames(
          styles.categories,
          styles[categoriesLayoutClass(group.layout)],
        )}
      >
        {group.categories.map((category) => {
          const values = criteria?.[category.field] ?? [];
          const hasValues = values.length > 0;

          // Read-only view of real data: an empty (optional) field is hidden
          // entirely. The blurred placeholder is only the pre-generation teaser.
          if (!onSaveField && !hasValues && !isPlaceholder) {
            return null;
          }

          return (
            <div
              key={category.field}
              className={styles.category}
              style={
                category.color
                  ? ({ "--category-color": category.color } as CSSProperties)
                  : undefined
              }
            >
              {showCategoryHeader && (
                <span className={styles.categoryLabel}>{T(category.labelKey)}</span>
              )}

              {onSaveField ? (
                <ProspectStrategyValues
                  values={values}
                  display={category.display}
                  lang={lang}
                  color={category.color}
                  onSave={(items) => onSaveField(category.field, items)}
                />
              ) : hasValues ? (
                <ProspectStrategyValues
                  values={values}
                  display={category.display}
                  lang={lang}
                  color={category.color}
                />
              ) : (
                <BlurredPlaceholder
                  className={styles.placeholder}
                  rowClassName={styles.placeholderRow}
                  rows={category.placeholderWidths.map((width) => ({
                    cells: [{ width }],
                  }))}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
